import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    Link,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { columnsBadge } from '../state/traderColumnsPref';
import type { TraderColumnsView } from '../state/useTraderColumns';
import { TRADER_COLUMNS, TRADER_COLUMN_GROUPS } from './traderColumns';

export interface TraderColumnsMenuProps {
    anchor: HTMLElement | null;
    onClose: () => void;
    view: TraderColumnsView;
    sortableKeys: readonly string[];
}

const MUTED_SX = {
    fontSize: '10px',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
} as const;

// Spec 4.4: one list per group, a checkbox per column, Fit to width and
// Show all in the footer.
export function TraderColumnsMenu({
    anchor,
    onClose,
    view,
    sortableKeys,
}: TraderColumnsMenuProps) {
    const shown = new Set(view.columns.map(c => c.key));
    const auto = view.mode === 'auto';
    return (
        <Popover
            open={anchor !== null}
            anchorEl={anchor}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
                paper: {
                    role: 'dialog',
                    'aria-label': 'Columns',
                    sx: { width: 660, maxWidth: 'calc(100vw - 32px)', p: 1.75 },
                },
            }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}
            >
                <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                    Columns
                </Typography>
                <Typography sx={MUTED_SX}>
                    {columnsBadge(view.mode, view.columns.length)}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" aria-label="Close" onClick={onClose}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 1.5,
                }}
            >
                {TRADER_COLUMN_GROUPS.map(group => {
                    const columns = TRADER_COLUMNS.filter(
                        c => c.group === group.key
                    );
                    const allShown = columns.every(c => shown.has(c.key));
                    return (
                        <Box key={group.key} sx={{ minWidth: 0 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    pb: 0.5,
                                    mb: 0.5,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {group.label}
                                </Typography>
                                <Link
                                    component="button"
                                    type="button"
                                    underline="hover"
                                    aria-label={`${allShown ? 'Hide' : 'Show'} all ${group.label} columns`}
                                    sx={{ fontSize: '11px' }}
                                    onClick={() =>
                                        allShown
                                            ? view.hideGroup(group.key)
                                            : view.showGroup(group.key)
                                    }
                                >
                                    {allShown ? 'Hide all' : 'Show all'}
                                </Link>
                            </Box>
                            {columns.map(column => {
                                const on = shown.has(column.key);
                                const sortable = Boolean(
                                    column.sort &&
                                        sortableKeys.includes(column.sort)
                                );
                                return (
                                    <FormControlLabel
                                        key={column.key}
                                        sx={{
                                            display: 'flex',
                                            mx: 0,
                                            height: 28,
                                        }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={on}
                                                onChange={() =>
                                                    on
                                                        ? view.hide(column.key)
                                                        : view.show(column.key)
                                                }
                                                sx={{ p: 0.5 }}
                                            />
                                        }
                                        label={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    fontSize: '12px',
                                                }}
                                            >
                                                {/* The spaces keep the parts apart in the checkbox's accessible name. */}
                                                <span>{column.label}</span>
                                                {!sortable && (
                                                    <>
                                                        {' '}
                                                        <Box
                                                            component="span"
                                                            sx={MUTED_SX}
                                                        >
                                                            no sort
                                                        </Box>
                                                    </>
                                                )}{' '}
                                                <Tooltip
                                                    title="Priority for the automatic fit"
                                                    describeChild
                                                    disableInteractive
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={MUTED_SX}
                                                    >{`#${column.priority}`}</Box>
                                                </Tooltip>
                                            </Box>
                                        }
                                        slotProps={{
                                            typography: {
                                                component: 'div',
                                                sx: { flex: 1, minWidth: 0 },
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    );
                })}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    borderTop: 1,
                    borderColor: 'divider',
                    pt: 1.25,
                    mt: 1.25,
                }}
            >
                <Button
                    size="small"
                    variant="outlined"
                    aria-pressed={auto}
                    onClick={view.fitToWidth}
                    sx={{
                        textTransform: 'none',
                        ...(auto ? { bgcolor: 'action.selected' } : {}),
                    }}
                >
                    Fit to width
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    aria-label="Show every column"
                    onClick={view.showAll}
                    sx={{ textTransform: 'none' }}
                >
                    Show all
                </Button>
                <Typography
                    sx={{
                        flex: 1,
                        minWidth: 220,
                        textAlign: 'right',
                        fontSize: '11px',
                        color: 'text.secondary',
                    }}
                >
                    {auto
                        ? 'Auto shows the highest-priority columns that fit and follows the window width. Picking a column yourself switches to your own set, saved in this browser.'
                        : 'Your own set, saved in this browser. Fit to width goes back to the automatic choice.'}
                </Typography>
            </Box>
        </Popover>
    );
}
